/**
 * Landing — public marketing page at juuniai.com/
 *
 * Shown to unauthenticated visitors via ProtectedRoute's anon-at-root
 * branch. Authenticated users are redirected to their role-appropriate
 * dashboard before they ever see this.
 *
 * Implementation: full-viewport iframe pointing at `/landing.html` (the
 * Claude Design handoff, shipped as a static asset in public/).
 *
 * Why iframe instead of porting the HTML into React:
 *   - The landing is ~1,150 lines of self-contained HTML + inline CSS +
 *     inline JS, with a hand-tuned SVG clock, scroll-synced kanji
 *     backgrounds, and five chapter diagrams. Porting into React would
 *     require babysitting every animation and risk subtle drift from the
 *     design tool preview the user authored it in.
 *   - Iframe preserves pixel-perfect fidelity with claude.ai/design.
 *   - `public/landing.html` has `<base target="_top">` in its head, so
 *     the nav "Sign in" link (href="/login") and the finale "Request
 *     access" CTAs (href="/request-access") navigate the PARENT window —
 *     they land on the SPA's routes, not inside the iframe.
 *
 * Why this is mounted via ProtectedRoute at `/` instead of a Vercel
 * rewrite to /landing.html directly:
 *   - Vercel's filesystem routing serves `dist/index.html` (the SPA
 *     entry) for `/` BEFORE evaluating rewrites in vercel.json, so
 *     `{"source": "/", "destination": "/landing.html"}` doesn't fire.
 *     Mounting the iframe at the SPA's `/` route sidesteps that — React
 *     Router owns `/`, this component runs, and then the iframe loads
 *     /landing.html as a static asset.
 *
 * 2026-05-03 — Phase 23 deep-dive #30 followup (LANDING_CACHE_BUST):
 *
 *   The iframe's URL is suffixed with a build-time cache-bust token
 *   (`?v=<BUILD_ID>`). This is NOT for content versioning — it's a
 *   defense against a specific failure mode that bit production:
 *
 *   When Phase 19 Tier-2 hardening shipped `X-Frame-Options: DENY`
 *   (later reverted in dive #30), browsers cached the BLOCKED iframe
 *   load decision. Even after the headers were corrected to SAMEORIGIN,
 *   browsers held onto the "this URL is blocked" state in memory cache
 *   keyed by the bare URL `/landing.html`. Hard reload didn't always
 *   clear it. New deploys didn't change the iframe URL, so the cache
 *   key stayed the same. End-state: prod-grade headers correct, prod
 *   still showed blank for users who'd visited during the broken
 *   window.
 *
 *   Cache-bust on the iframe src forces a fresh URL on every component
 *   mount, bypassing any cached block-decision. Token is a per-mount
 *   constant (Date.now() in useMemo with [] deps) so it's stable for
 *   the page's lifetime but changes when the user visits in a fresh
 *   tab/reload — guaranteed-fresh iframe URL on every visit.
 *
 *   Why per-mount (not per-render): if the cache-bust changed on every
 *   render, React would re-mount the iframe perpetually causing reload
 *   loops + lost scroll position + animation restart. useMemo([], []) =
 *   stable token, computed once when the component first mounts.
 *
 *   Trade-offs:
 *     - Negligible cache benefit on the iframe content (tiny ~100KB
 *       static HTML, must-revalidate already, ETag matches rapidly).
 *     - One extra query-string round-trip on every visit.
 *     - Defense against an entire class of browser-cached-state bugs.
 */

import { useMemo } from 'react';

export default function Landing() {
  // Cache-bust token: stable per mount. See header comment for the
  // exact failure mode this defends against.
  const cacheBust = useMemo(() => Date.now().toString(36), []);
  return (
    <iframe
      src={`/landing.html?v=${cacheBust}`}
      title="Juuni — the ops layer for AI data"
      // Phase 15 Tier-2 §2.7: lvw/lvh (large viewport) for fixed-position
      // landing iframe — gives MAXIMUM stretch when iOS Safari address bar
      // collapses on scroll, preventing layout reflow during chrome
      // transitions. dvh/dvw would cause iframe to flicker as user scrolls.
      // The landing relies on position:fixed for nav, clock stage, rail,
      // and kanji backgrounds — it needs the full viewport as its
      // coordinate space. border:none + margin:0 keep the iframe visually
      // invisible.
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100lvw',
        height: '100lvh',
        border: 'none',
        margin: 0,
        display: 'block',
      }}
    />
  );
}
